<template>
    <HeaderContent title="Admin"/>
    <ActionContent
        ctaText="Admin"
        :filterDate="false"
        :filterModal="false"
        @action="modal = !modal"
        @search-query="searchData"
    />
    <AdminForm
        :modal="modal"
        :selectedData="selectedData"
        @update:modal="updateModal"
        @addOrUpdate="getAll"
    ></AdminForm>
    <DataTable
        :data="adminList"
        :header="headers"
        :custom-actions="customActions"
    >
        <template #cell(status)="{ item }">
             <span
                class="badge"
                :class="item.status === 'Active' ? 'bg-success' : 'bg-danger'"
            >
                {{ item.status }}
            </span>
        </template>
    </DataTable>
</template>

<script setup>
import axios from "axios";
import HeaderContent from '@/components/HeaderContent.vue';
import ActionContent from '@/components/ActionContent.vue';
import AdminForm from './AdminForm.vue';
import DataTable from '@/components/DataTable.vue';
import { ref, onMounted, inject } from 'vue';
import { useLoading } from "vue-loading-overlay";
import { useAuthStore } from "@/store/auth.js";
import { useToast } from "vue-toastification";

const auth = useAuthStore();
const $loading = useLoading();
const selectedData = ref({});
const swal = inject("$swal");
const toast = useToast();

const modal = ref(false);
const adminList = ref([]);

const headers = ref([
    { key: "no", label: "No", sortable: true, class: "text-center" },
    { key: "name", label: "Name", sortable: true, class: "text-left" },
    { key: "email", label: "Email", sortable: true, class: "text-left" },
    { key: "phone", label: "Phone Number", sortable: true, class: "text-left" },
    { key: "roles_name", label: "Position", sortable: true, class: "text-left" },
    { key: "status", label: "Status", sortable: false, class: "text-center" },
    { key: "action", label: "Actions", class: "text-center" },
]);

const customActions = ref([
    {
        label: (item) => item.status === 'Active' ? 'Deactive' : 'Active',
        variant: (item) => item.status === 'Active' ? 'warning' : 'primary',
        icon:   (item) => item.status === 'Active' ? 'fas fa-user-times' : 'fas fa-user-check',
        action: (item) => {
            const newStatus = item.status === 'Active' ? 'Non Active' : 'Active';
            updateStatus(item.uuid, newStatus);
        },
    },
    {
        label: "Edit",
        variant: "success",
        icon: "fas fa-edit",
        action: (item) => {
            selectedData.value = item;
            modal.value = true;
        },
    },
    {
        label: "Delete",
        variant: "danger",
        icon: "fas fa-trash",
        action: (item) => {
            swal({
                title: "Confirm",
                text: "Are you sure to delete this user?",
                showCancelButton: true,
                confirmButtonText: "Yes, delete!",
                cancelButtonText: "Cancel",
                customClass: {
                    popup: "rounded",
                    confirmButton: "btn btn-danger me-2",
                    cancelButton: "btn btn-secondary",
                },
                buttonsStyling: false,
            }).then((confirm) => {
                if (confirm.value) {
                    deleteData(item.uuid);
                } else {
                    swal.close();
                }
            });
        },
    },
]);

const searchData = async (search) => {
    const loader = $loading.show({
        color: "#489464",
        loader: "spinner",
    });
    try {
        await getAll(search);
    } finally {
        loader.hide();
    }
};

const updateModal = (newValue) => {
    modal.value = newValue;
    if (!newValue) {
        selectedData.value = {};
    }
};

const getAll = async (search = "") => {
    try {
        const response = await axios.get(`${process.env.VUE_APP_API_URL}/admin`, {
            params: {
                search: search,
            },
            headers: {
                "Content-type": "application/json",
                Authorization: "Bearer " + auth._token,
            },
        });

        const res = response.data;

        if (res.status) {
            adminList.value = res.data.data.map((item) => {
                return {
                    ...item,
                    roles_name: item.roles[0].name,
                };
            });
        }
    } catch (error) {
        console.error("Failed to fetch admins", error);
    }
};

const updateStatus = async (uuid, status) => {
    const loader = $loading.show({
        color: "#489464",
        loader: "spinner",
    });
    try {
        const response = await axios.put(
            `${process.env.VUE_APP_API_URL}/admin/status/${uuid}`,
            { status: status },
            {
                headers: {
                    "Content-type": "application/json",
                    Authorization: "Bearer " + auth._token,
                },
            }
        );

        const res = response.data;
        if (res.status) {
            toast.success(res.message);
            getAll();
        }
    } catch (error) {
        toast.error(error.response?.data?.message || "Failed to update status");
    } finally {
        loader.hide();
    }
};

const deleteData = async (uuid) => {
    if (uuid) {
        toast.clear();
        const loader = $loading.show({
            color: "#009689",
            loader: "bars",
            zIndex: 999999,
        });

        try {
            const response = await axios.delete(
                `${process.env.VUE_APP_API_URL}/admin/delete/${uuid}`,
                {
                    headers: {
                        "Content-type": "application/json",
                        Authorization: "Bearer " + auth._token,
                    },
                }
            );

            const res = response.data;

            if (res.status) {
                toast.success(res.message);
                getAll();
            }
        } catch (error) {
             toast.error(error.response?.data?.message || "Failed to delete user");
        } finally {
            loader.hide();
        }
    }
};

onMounted(async () => {
    const loader = $loading.show({
        color: "#489464",
        loader: "spinner",
    });
    try {
        await getAll();
    } finally {
        loader.hide();
    }
});
</script>
